import React, { useState, useEffect } from 'react';
import { Button } from '../ui';
import { loadCompanyProfile } from '../settings/CompanyProfileModal';
import { loadSavedClients, findClientByName, addClient } from '../settings/ClientManager';
import { exportEstimateToPDF } from '../../utils/exportPDF';

/**
 * Generate unique ID for line items
 */
const generateId = () => Math.random().toString(36).substr(2, 9);

/**
 * Default unit prices for common materials (estimates, can be edited)
 */
const DEFAULT_PRICES = {
  // Flooring
  hardwood: 8.00,
  vinyl_plank: 4.50,
  tile_12x12: 3.50,
  tile_18x18: 4.00,
  carpet: 3.00,
  underlayment: 0.50,
  baseboards: 2.50,
  // Paint
  wall_paint: 35.00,
  wall_paint_2coat: 35.00,
  ceiling_paint: 30.00,
  primer: 25.00,
  painters_tape: 8.00,
  // Drywall
  drywall_walls: 12.00,
  drywall_ceiling: 12.00,
  joint_compound: 15.00,
  drywall_tape: 5.00,
  drywall_screws: 8.00,
  // Tile
  wall_tile: 4.00,
  thinset: 20.00,
  grout: 15.00,
  // Insulation
  batt_r13_walls: 0.80,
  batt_r30_ceiling: 1.20,
  blown_in: 1.50,
  // Concrete
  concrete_yards: 150.00,
  concrete_bags: 6.00,
  rebar: 0.75,
  wire_mesh: 50.00,
  gravel_base: 45.00,
  // Framing
  studs: 6.00,
  studs_2x4: 5.50,
  studs_2x6: 8.00,
  top_plates: 8.00,
  top_plates_2x4: 7.00,
  top_plates_2x6: 10.00,
  bottom_plate: 8.00,
  bottom_plate_2x4: 7.00,
  bottom_plate_2x6: 10.00,
  sheathing: 25.00,
  sheathing_osb: 25.00,
  framing_nails: 50.00,
  sheathing_nails: 45.00,
  blocking: 3.00,
  blocking_2x4: 3.00,
  blocking_2x6: 4.50,
};

/**
 * EstimateEditor - Full-screen editor for creating estimates from materials
 */
const EstimateEditor = ({
  isOpen,
  onClose,
  onSave,
  initialMaterials = [],
  existingEstimate = null,
  projectName = 'Floor Plan',
  isMobile = false,
}) => {
  // Mobile tab state - 'items' | 'details'
  const [mobileTab, setMobileTab] = useState('items');

  // Form state
  const [estimateName, setEstimateName] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [estimateDate, setEstimateDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [scopeOfWork, setScopeOfWork] = useState('');
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [laborItems, setLaborItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [showTax, setShowTax] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);

  // Client management state
  const [savedClients, setSavedClients] = useState([]);
  const [showSaveClientPrompt, setShowSaveClientPrompt] = useState(false);
  const [pendingEstimate, setPendingEstimate] = useState(null);

  // Load company profile and saved clients
  useEffect(() => {
    if (isOpen) {
      const profile = loadCompanyProfile();
      setCompanyProfile(profile);
      setSavedClients(loadSavedClients());
    }
  }, [isOpen]);

  // Initialize from materials or existing estimate
  useEffect(() => {
    if (!isOpen) return;

    // Default terms text
    const defaultTerms = `• 50% deposit required to begin work
• Balance due upon completion
• Price valid for 30 days from estimate date
• Any changes to scope may affect final price`;

    // Get today's date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];
    // Default valid for 30 days
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    if (existingEstimate) {
      // Load existing estimate
      setEstimateName(existingEstimate.name || '');
      setClientName(existingEstimate.clientName || '');
      setClientEmail(existingEstimate.clientEmail || '');
      setClientPhone(existingEstimate.clientPhone || '');
      setClientAddress(existingEstimate.clientAddress || '');
      setEstimateDate(existingEstimate.estimateDate || today);
      setValidUntil(existingEstimate.validUntil || '');
      setScopeOfWork(existingEstimate.scopeOfWork || '');
      setTermsAndConditions(existingEstimate.termsAndConditions || '');
      setLineItems(existingEstimate.lineItems || []);
      setLaborItems(existingEstimate.laborItems || []);
      setTaxRate(existingEstimate.taxRate || 0);
      setShowTax(existingEstimate.taxRate > 0);
    } else if (initialMaterials.length > 0) {
      // Create line items from materials
      const items = initialMaterials
        .filter(m => m.quantity > 0)
        .map(material => ({
          id: generateId(),
          name: material.name,
          description: material.unitSize ? `${material.unitSize}` : '',
          quantity: Math.ceil(material.quantity),
          unit: material.unit,
          unitPrice: DEFAULT_PRICES[material.id] || 0,
          materialId: material.id,
        }));
      setLineItems(items);
      setLaborItems([]);
      setEstimateName(`${projectName} - Estimate`);
      setClientName('');
      setClientEmail('');
      setClientPhone('');
      setClientAddress('');
      setEstimateDate(today);
      setValidUntil(thirtyDaysFromNow);
      setScopeOfWork('');
      setTermsAndConditions(defaultTerms);
      setTaxRate(0);
      setShowTax(false);
    } else {
      // Reset for new estimate
      setEstimateName(`${projectName} - Estimate`);
      setClientName('');
      setClientEmail('');
      setClientPhone('');
      setClientAddress('');
      setEstimateDate(today);
      setValidUntil(thirtyDaysFromNow);
      setScopeOfWork('');
      setTermsAndConditions(defaultTerms);
      setLineItems([]);
      setLaborItems([]);
      setTaxRate(0);
      setShowTax(false);
    }
  }, [isOpen, initialMaterials, existingEstimate, projectName]);

  if (!isOpen) return null;

  // Calculate totals
  const materialsSubtotal = lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const laborSubtotal = laborItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const subtotal = materialsSubtotal + laborSubtotal;
  const taxAmount = showTax ? subtotal * (taxRate / 100) : 0;
  const total = subtotal + taxAmount;

  // Handle line item changes
  const updateLineItem = (id, field, value) => {
    setLineItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const deleteLineItem = (id) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, {
      id: generateId(),
      name: '',
      description: '',
      quantity: 1,
      unit: 'ea',
      unitPrice: 0,
      materialId: null,
    }]);
  };

  // Handle labor item changes
  const updateLaborItem = (id, field, value) => {
    setLaborItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const deleteLaborItem = (id) => {
    setLaborItems(prev => prev.filter(item => item.id !== id));
  };

  const addLaborItem = () => {
    setLaborItems(prev => [...prev, {
      id: generateId(),
      description: '',
      amount: 0,
    }]);
  };

  // Handle selecting a saved client
  const handleSelectClient = (clientId) => {
    if (!clientId) {
      // "New Client" selected - clear fields
      setClientName('');
      setClientEmail('');
      setClientPhone('');
      setClientAddress('');
      return;
    }
    const client = savedClients.find(c => c.id === clientId);
    if (client) {
      setClientName(client.name || '');
      setClientEmail(client.email || '');
      setClientPhone(client.phone || '');
      setClientAddress(client.address || '');
    }
  };

  // Create estimate object
  const createEstimateObject = () => ({
    id: existingEstimate?.id || generateId(),
    name: estimateName,
    clientName,
    clientEmail,
    clientPhone,
    clientAddress,
    estimateDate,
    validUntil,
    scopeOfWork,
    termsAndConditions,
    lineItems,
    laborItems,
    taxRate: showTax ? taxRate : 0,
    materialsSubtotal,
    laborSubtotal,
    subtotal,
    taxAmount,
    total,
    status: existingEstimate?.status || 'draft',
    createdAt: existingEstimate?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  // Handle save
  const handleSave = () => {
    const estimate = createEstimateObject();

    // Check if this is a new client that should be saved
    if (clientName.trim() && !findClientByName(clientName)) {
      // New client - prompt to save
      setPendingEstimate(estimate);
      setShowSaveClientPrompt(true);
      return;
    }

    // No new client or client already exists, just save
    onSave?.(estimate);
    onClose();
  };

  // Handle save client prompt response
  const handleSaveClientResponse = (shouldSave) => {
    if (shouldSave && clientName.trim()) {
      addClient({
        name: clientName.trim(),
        email: clientEmail.trim(),
        phone: clientPhone.trim(),
        address: clientAddress.trim(),
      });
    }
    // Save the estimate
    if (pendingEstimate) {
      onSave?.(pendingEstimate);
    }
    setShowSaveClientPrompt(false);
    setPendingEstimate(null);
    onClose();
  };

  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${estimateName || 'Estimate'}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #333; }
            .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
            .company { }
            .company-logo { max-width: 120px; max-height: 60px; margin-bottom: 8px; }
            .company-name { font-size: 20px; font-weight: 700; color: #1a2332; }
            .company-info { font-size: 11px; color: #666; line-height: 1.5; }
            .estimate-title { font-size: 28px; font-weight: 300; color: #666; text-align: right; }
            .estimate-date { font-size: 12px; color: #666; text-align: right; margin-top: 4px; }
            .client-section { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            .client-label { font-size: 10px; text-transform: uppercase; color: #666; margin-bottom: 4px; }
            .client-name { font-size: 16px; font-weight: 600; }
            .client-details { font-size: 12px; color: #666; margin-top: 4px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
            th { background: #1a2332; color: #fff; padding: 12px; text-align: left; font-size: 11px; text-transform: uppercase; }
            th:last-child { text-align: right; }
            td { padding: 12px; border-bottom: 1px solid #eee; font-size: 13px; }
            td:last-child { text-align: right; }
            .item-name { font-weight: 500; }
            .item-desc { font-size: 11px; color: #666; }
            .section-title { font-size: 14px; font-weight: 600; color: #1a2332; margin: 30px 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #00c8ff; }
            .subtotal-row { display: flex; justify-content: space-between; padding: 10px 12px; background: #f8f9fa; border-radius: 4px; margin-bottom: 20px; font-weight: 500; }
            .totals { margin-left: auto; width: 300px; margin-top: 20px; }
            .total-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
            .total-row.grand { font-size: 18px; font-weight: 700; border-bottom: 2px solid #1a2332; border-top: 2px solid #1a2332; background: #f8f9fa; padding: 12px; margin-top: 8px; }
            .notes { margin-top: 40px; padding: 20px; background: #f8f9fa; border-radius: 8px; }
            .notes-label { font-size: 10px; text-transform: uppercase; color: #666; margin-bottom: 8px; }
            .notes-text { font-size: 12px; color: #666; white-space: pre-wrap; }
            .footer { margin-top: 60px; text-align: center; font-size: 11px; color: #999; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">
              ${companyProfile?.logo ? `<img src="${companyProfile.logo}" class="company-logo" />` : ''}
              <div class="company-name">${companyProfile?.companyName || 'Your Company'}</div>
              <div class="company-info">
                ${companyProfile?.phone ? `${companyProfile.phone}<br>` : ''}
                ${companyProfile?.email ? `${companyProfile.email}<br>` : ''}
                ${companyProfile?.address ? companyProfile.address.replace(/\n/g, '<br>') : ''}
                ${companyProfile?.website ? `<br>${companyProfile.website}` : ''}
                ${companyProfile?.licenseNumber ? `<br>${companyProfile.licenseNumber}` : ''}
              </div>
            </div>
            <div>
              <div class="estimate-title">ESTIMATE</div>
              <div class="estimate-date">Date: ${estimateDate ? new Date(estimateDate + 'T00:00:00').toLocaleDateString() : new Date().toLocaleDateString()}</div>
              ${validUntil ? `<div class="estimate-date">Valid Until: ${new Date(validUntil + 'T00:00:00').toLocaleDateString()}</div>` : ''}
            </div>
          </div>

          ${estimateName ? `<h2 style="font-size: 18px; font-weight: 600; color: #1a2332; margin-bottom: 20px;">${estimateName}</h2>` : ''}

          ${clientName ? `
          <div class="client-section">
            <div class="client-label">Prepared For</div>
            <div class="client-name">${clientName}</div>
            <div class="client-details">
              ${clientAddress ? clientAddress.replace(/\n/g, '<br>') : ''}
              ${clientPhone ? `<br>${clientPhone}` : ''}
              ${clientEmail ? `<br>${clientEmail}` : ''}
            </div>
          </div>
          ` : ''}

          ${lineItems.length > 0 ? `
          <h3 class="section-title">Materials</h3>
          <table>
            <thead>
              <tr>
                <th style="width: 40%">Item</th>
                <th style="width: 15%">Qty</th>
                <th style="width: 15%">Unit</th>
                <th style="width: 15%">Unit Price</th>
                <th style="width: 15%">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${lineItems.map(item => `
                <tr>
                  <td>
                    <div class="item-name">${item.name}</div>
                    ${item.description ? `<div class="item-desc">${item.description}</div>` : ''}
                  </td>
                  <td>${item.quantity}</td>
                  <td>${item.unit}</td>
                  <td>$${item.unitPrice.toFixed(2)}</td>
                  <td>$${(item.quantity * item.unitPrice).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="subtotal-row">
            <span>Materials Subtotal</span>
            <span>$${materialsSubtotal.toFixed(2)}</span>
          </div>
          ` : ''}

          ${laborItems.length > 0 ? `
          <h3 class="section-title">Labor</h3>
          <table>
            <thead>
              <tr>
                <th style="width: 70%">Description</th>
                <th style="width: 30%">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${laborItems.map(item => `
                <tr>
                  <td>${item.description}</td>
                  <td>$${(item.amount || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="subtotal-row">
            <span>Labor Subtotal</span>
            <span>$${laborSubtotal.toFixed(2)}</span>
          </div>
          ` : ''}

          <div class="totals">
            <div class="total-row">
              <span>Subtotal</span>
              <span>$${subtotal.toFixed(2)}</span>
            </div>
            ${showTax && taxRate > 0 ? `
            <div class="total-row">
              <span>Tax (${taxRate}%)</span>
              <span>$${taxAmount.toFixed(2)}</span>
            </div>
            ` : ''}
            <div class="total-row grand">
              <span>Total</span>
              <span>$${total.toFixed(2)}</span>
            </div>
          </div>

          ${scopeOfWork ? `
          <div class="notes">
            <div class="notes-label">Scope of Work</div>
            <div class="notes-text">${scopeOfWork}</div>
          </div>
          ` : ''}

          ${termsAndConditions ? `
          <div class="notes" style="margin-top: 20px;">
            <div class="notes-label">Terms & Conditions</div>
            <div class="notes-text">${termsAndConditions}</div>
          </div>
          ` : ''}

          <div class="footer">
            Thank you for considering our services.
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    const estimateData = {
      name: estimateName,
      clientName,
      clientEmail,
      clientPhone,
      clientAddress,
      estimateDate,
      validUntil,
      lineItems,
      laborItems,
      materialsSubtotal,
      laborSubtotal,
      subtotal,
      taxRate: showTax ? taxRate : 0,
      taxAmount,
      total,
      scopeOfWork,
      termsAndConditions,
    };

    try {
      await exportEstimateToPDF(estimateData, { companyProfile });
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF. Please try again.');
    }
  };

  // Mobile-specific line item component for better touch interaction
  const MobileLineItem = ({ item, onUpdate, onDelete }) => (
    <div style={mobileStyles.lineItem}>
      <div style={mobileStyles.lineItemHeader}>
        <input
          type="text"
          value={item.name}
          onChange={(e) => onUpdate(item.id, 'name', e.target.value)}
          style={mobileStyles.itemNameInput}
          placeholder="Item name"
        />
        <Button variant="danger" size="large" onClick={() => onDelete(item.id)} style={{ padding: '10px 14px' }}>
          ×
        </Button>
      </div>
      <input
        type="text"
        value={item.description}
        onChange={(e) => onUpdate(item.id, 'description', e.target.value)}
        style={mobileStyles.descInput}
        placeholder="Description (optional)"
      />
      <div style={mobileStyles.lineItemRow}>
        <div style={mobileStyles.fieldGroup}>
          <label style={mobileStyles.fieldLabel}>Qty</label>
          <input
            type="number"
            value={item.quantity}
            onChange={(e) => onUpdate(item.id, 'quantity', parseFloat(e.target.value) || 0)}
            style={mobileStyles.smallInput}
            min="0"
          />
        </div>
        <div style={mobileStyles.fieldGroup}>
          <label style={mobileStyles.fieldLabel}>Unit</label>
          <input
            type="text"
            value={item.unit}
            onChange={(e) => onUpdate(item.id, 'unit', e.target.value)}
            style={mobileStyles.smallInput}
          />
        </div>
        <div style={mobileStyles.fieldGroup}>
          <label style={mobileStyles.fieldLabel}>Price</label>
          <input
            type="number"
            value={item.unitPrice}
            onChange={(e) => onUpdate(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
            style={mobileStyles.smallInput}
            min="0"
            step="0.01"
          />
        </div>
        <div style={mobileStyles.lineTotal}>
          ${(item.quantity * item.unitPrice).toFixed(2)}
        </div>
      </div>
    </div>
  );

  // Mobile-specific labor item component
  const MobileLaborItem = ({ item, onUpdate, onDelete }) => (
    <div style={mobileStyles.laborItem}>
      <div style={mobileStyles.laborItemHeader}>
        <input
          type="text"
          value={item.description}
          onChange={(e) => onUpdate(item.id, 'description', e.target.value)}
          style={mobileStyles.itemNameInput}
          placeholder="Labor description"
        />
        <Button variant="danger" size="large" onClick={() => onDelete(item.id)} style={{ padding: '10px 14px' }}>
          ×
        </Button>
      </div>
      <div style={mobileStyles.laborAmountRow}>
        <span style={mobileStyles.fieldLabel}>Amount</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ color: '#6080a0' }}>$</span>
          <input
            type="number"
            value={item.amount}
            onChange={(e) => onUpdate(item.id, 'amount', parseFloat(e.target.value) || 0)}
            style={{ ...mobileStyles.smallInput, width: '100px' }}
            min="0"
            step="0.01"
          />
        </div>
      </div>
    </div>
  );

  // MOBILE LAYOUT
  if (isMobile) {
    return (
      <div style={styles.overlay}>
        <div style={mobileStyles.modal}>
          {/* Mobile Header */}
          <div style={mobileStyles.header}>
            <Button variant="danger" size="medium" onClick={onClose} style={{ padding: '8px 12px' }}>
              ✕
            </Button>
            <span style={mobileStyles.headerTitle}>Estimate</span>
            <div style={mobileStyles.headerActions}>
              <Button variant="default" size="medium" onClick={handleExportPDF} title="Export PDF">
                📄
              </Button>
              <Button variant="default" size="medium" onClick={handlePrint} title="Print">
                🖨️
              </Button>
            </div>
          </div>

          {/* Tab Bar */}
          <div style={mobileStyles.tabBar}>
            <Button
              variant="ghost"
              active={mobileTab === 'items'}
              onClick={() => setMobileTab('items')}
              style={{
                flex: 1,
                borderRadius: 0,
                borderBottomWidth: '2px',
                borderBottomStyle: 'solid',
                borderBottomColor: mobileTab === 'items' ? '#00c8ff' : 'transparent',
                padding: '14px 16px',
              }}
            >
              Items & Totals
            </Button>
            <Button
              variant="ghost"
              active={mobileTab === 'details'}
              onClick={() => setMobileTab('details')}
              style={{
                flex: 1,
                borderRadius: 0,
                borderBottomWidth: '2px',
                borderBottomStyle: 'solid',
                borderBottomColor: mobileTab === 'details' ? '#00c8ff' : 'transparent',
                padding: '14px 16px',
              }}
            >
              Details
            </Button>
          </div>

          {/* Tab Content */}
          <div style={mobileStyles.content}>
            {mobileTab === 'items' ? (
              /* Items Tab */
              <div style={mobileStyles.scrollContainer}>
                {/* Estimate Name */}
                <div style={mobileStyles.field}>
                  <label style={mobileStyles.label}>Estimate Name</label>
                  <input
                    type="text"
                    value={estimateName}
                    onChange={(e) => setEstimateName(e.target.value)}
                    style={mobileStyles.input}
                    placeholder="Enter estimate name..."
                  />
                </div>

                {/* Line Items Section */}
                <div style={mobileStyles.section}>
                  <div style={mobileStyles.sectionHeader}>
                    <span style={mobileStyles.sectionTitle}>Materials</span>
                    <Button variant="primary" size="medium" onClick={addLineItem}>+ Add</Button>
                  </div>

                  {lineItems.length === 0 ? (
                    <div style={mobileStyles.emptyState}>
                      No items yet. Tap "+ Add" to add materials.
                    </div>
                  ) : (
                    lineItems.map(item => (
                      <MobileLineItem
                        key={item.id}
                        item={item}
                        onUpdate={updateLineItem}
                        onDelete={deleteLineItem}
                      />
                    ))
                  )}

                  {lineItems.length > 0 && (
                    <div style={mobileStyles.subtotalBar}>
                      <span>Materials Subtotal</span>
                      <span>${materialsSubtotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Labor Section */}
                <div style={mobileStyles.section}>
                  <div style={mobileStyles.sectionHeader}>
                    <span style={mobileStyles.sectionTitle}>Labor</span>
                    <Button variant="primary" size="medium" onClick={addLaborItem}>+ Add</Button>
                  </div>

                  {laborItems.length === 0 ? (
                    <div style={mobileStyles.emptyState}>
                      No labor items. Tap "+ Add" to include labor costs.
                    </div>
                  ) : (
                    <>
                      {laborItems.map(item => (
                        <MobileLaborItem
                          key={item.id}
                          item={item}
                          onUpdate={updateLaborItem}
                          onDelete={deleteLaborItem}
                        />
                      ))}
                      <div style={mobileStyles.subtotalBar}>
                        <span>Labor Subtotal</span>
                        <span>${laborSubtotal.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Totals Section */}
                <div style={mobileStyles.totalsSection}>
                  <div style={mobileStyles.totalRow}>
                    <span>Subtotal (Materials + Labor)</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>

                  <div style={mobileStyles.taxRow}>
                    <label style={mobileStyles.taxLabel}>
                      <input
                        type="checkbox"
                        checked={showTax}
                        onChange={(e) => setShowTax(e.target.checked)}
                        style={{ width: '20px', height: '20px', marginRight: '8px' }}
                      />
                      Add Tax
                    </label>
                    {showTax && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="number"
                          value={taxRate}
                          onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                          style={{ ...mobileStyles.smallInput, width: '60px' }}
                          min="0"
                          max="100"
                          step="0.1"
                        />
                        <span style={{ color: '#6080a0' }}>%</span>
                      </div>
                    )}
                  </div>
                  {showTax && taxRate > 0 && (
                    <div style={mobileStyles.totalRow}>
                      <span>Tax ({taxRate}%)</span>
                      <span>${taxAmount.toFixed(2)}</span>
                    </div>
                  )}

                  <div style={mobileStyles.grandTotalRow}>
                    <span>Total</span>
                    <span style={mobileStyles.grandTotalValue}>${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              /* Details Tab */
              <div style={mobileStyles.scrollContainer}>
                {/* Estimate Dates */}
                <div style={mobileStyles.section}>
                  <div style={mobileStyles.sectionTitle}>Estimate Details</div>

                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Estimate Date</label>
                    <input
                      type="date"
                      value={estimateDate}
                      onChange={(e) => setEstimateDate(e.target.value)}
                      style={mobileStyles.input}
                    />
                  </div>
                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Valid Until</label>
                    <input
                      type="date"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      style={mobileStyles.input}
                    />
                  </div>
                </div>

                {/* Client Information */}
                <div style={mobileStyles.section}>
                  <div style={mobileStyles.sectionTitle}>Client Information</div>

                  {savedClients.length > 0 && (
                    <div style={mobileStyles.field}>
                      <label style={mobileStyles.label}>Select Saved Client</label>
                      <select
                        onChange={(e) => handleSelectClient(e.target.value)}
                        style={mobileStyles.select}
                        value=""
                      >
                        <option value="">-- Select client --</option>
                        {savedClients.map(client => (
                          <option key={client.id} value={client.id}>
                            {client.name}{client.email ? ` (${client.email})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Client Name</label>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      style={mobileStyles.input}
                      placeholder="Client name"
                    />
                  </div>

                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Email</label>
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      style={mobileStyles.input}
                      placeholder="client@email.com"
                    />
                  </div>

                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Phone</label>
                    <input
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      style={mobileStyles.input}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Address (Optional)</label>
                    <textarea
                      value={clientAddress}
                      onChange={(e) => setClientAddress(e.target.value)}
                      style={mobileStyles.textarea}
                      placeholder="Client address"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Notes Section */}
                <div style={mobileStyles.section}>
                  <div style={mobileStyles.sectionTitle}>Notes</div>

                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Scope of Work</label>
                    <textarea
                      value={scopeOfWork}
                      onChange={(e) => setScopeOfWork(e.target.value)}
                      style={{ ...mobileStyles.textarea, minHeight: '100px' }}
                      placeholder="Describe the work to be performed..."
                      rows={4}
                    />
                  </div>

                  <div style={mobileStyles.field}>
                    <label style={mobileStyles.label}>Terms & Conditions</label>
                    <textarea
                      value={termsAndConditions}
                      onChange={(e) => setTermsAndConditions(e.target.value)}
                      style={{ ...mobileStyles.textarea, minHeight: '100px' }}
                      placeholder="Payment terms, conditions..."
                      rows={4}
                    />
                  </div>
                </div>

                {/* Company Preview */}
                {companyProfile?.companyName && (
                  <div style={mobileStyles.companyPreview}>
                    <div style={mobileStyles.previewLabel}>From</div>
                    <div style={mobileStyles.previewCompany}>{companyProfile.companyName}</div>
                    {companyProfile.licenseNumber && (
                      <div style={mobileStyles.previewLicense}>{companyProfile.licenseNumber}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile Footer */}
          <div style={mobileStyles.footer}>
            <Button variant="default" size="large" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button variant="primary" size="large" onClick={handleSave} style={{ flex: 2 }}>
              Save Estimate
            </Button>
          </div>

          {/* Save Client Prompt Modal */}
          {showSaveClientPrompt && (
            <div style={styles.promptOverlay}>
              <div style={styles.promptModal}>
                <div style={styles.promptIcon}>👤</div>
                <div style={styles.promptTitle}>Save New Client?</div>
                <div style={styles.promptText}>
                  Would you like to save <strong>{clientName}</strong> to your client list?
                </div>
                <div style={styles.promptButtons}>
                  <button
                    style={styles.promptButtonSecondary}
                    onClick={() => handleSaveClientResponse(false)}
                  >
                    No
                  </button>
                  <button
                    style={styles.promptButtonPrimary}
                    onClick={() => handleSaveClientResponse(true)}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // DESKTOP LAYOUT
  return (
    <div style={styles.overlay}>
      <div style={{
        ...styles.modal,
        width: '900px',
        height: '90vh',
        borderRadius: '12px',
      }}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={{ fontSize: '20px' }}>📝</span>
            <span style={styles.headerTitle}>Estimate Editor</span>
          </div>
          <div style={styles.headerRight}>
            <button style={styles.exportButton} onClick={handleExportPDF}>
              📄 Export PDF
            </button>
            <button style={styles.printButton} onClick={handlePrint}>
              🖨️ Print
            </button>
            <button style={styles.closeButton} onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {/* Left Column - Line Items */}
          <div style={styles.leftColumn}>
            {/* Estimate Name */}
            <div style={styles.field}>
              <label style={styles.label}>Estimate Name</label>
              <input
                type="text"
                value={estimateName}
                onChange={(e) => setEstimateName(e.target.value)}
                style={styles.input}
                placeholder="Enter estimate name..."
              />
            </div>

            {/* Line Items */}
            <div style={styles.lineItemsSection}>
              <div style={styles.lineItemsHeader}>
                <span style={styles.sectionTitle}>Line Items</span>
                <button style={styles.addButton} onClick={addLineItem}>
                  + Add Item
                </button>
              </div>

              {/* Column Headers */}
              <div style={styles.columnHeaders}>
                <span style={{ flex: 2 }}>Item</span>
                <span style={{ width: '60px', textAlign: 'center' }}>Qty</span>
                <span style={{ width: '60px', textAlign: 'center' }}>Unit</span>
                <span style={{ width: '80px', textAlign: 'right' }}>Price</span>
                <span style={{ width: '90px', textAlign: 'right' }}>Total</span>
                <span style={{ width: '32px' }}></span>
              </div>

              {/* Line Items List */}
              <div style={styles.lineItemsList}>
                {lineItems.length === 0 ? (
                  <div style={styles.emptyState}>
                    No items yet. Add materials from the calculator or add items manually.
                  </div>
                ) : (
                  lineItems.map(item => (
                    <div key={item.id} style={styles.lineItem}>
                      <div style={{ flex: 2 }}>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateLineItem(item.id, 'name', e.target.value)}
                          style={styles.itemInput}
                          placeholder="Item name"
                        />
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                          style={styles.descInput}
                          placeholder="Description (optional)"
                        />
                      </div>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                        style={{ ...styles.numberInput, width: '60px' }}
                        min="0"
                      />
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => updateLineItem(item.id, 'unit', e.target.value)}
                        style={{ ...styles.itemInput, width: '60px', textAlign: 'center' }}
                      />
                      <div style={{ width: '80px', position: 'relative' }}>
                        <span style={styles.dollarSign}>$</span>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => updateLineItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                          style={styles.priceInput}
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div style={styles.lineTotal}>
                        ${(item.quantity * item.unitPrice).toFixed(2)}
                      </div>
                      <button
                        style={styles.deleteButton}
                        onClick={() => deleteLineItem(item.id)}
                        title="Delete item"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Materials Subtotal */}
            {lineItems.length > 0 && (
              <div style={styles.sectionSubtotal}>
                <span>Materials Subtotal</span>
                <span>${materialsSubtotal.toFixed(2)}</span>
              </div>
            )}

            {/* Labor Section */}
            <div style={styles.laborSection}>
              <div style={styles.lineItemsHeader}>
                <span style={styles.sectionTitle}>Labor</span>
                <button style={styles.addButton} onClick={addLaborItem}>
                  + Add Labor
                </button>
              </div>

              {laborItems.length > 0 && (
                <>
                  {/* Labor Column Headers */}
                  <div style={styles.laborColumnHeaders}>
                    <span style={{ flex: 1 }}>Description</span>
                    <span style={{ width: '120px', textAlign: 'right' }}>Amount</span>
                    <span style={{ width: '32px' }}></span>
                  </div>

                  {/* Labor Items List */}
                  <div style={styles.laborItemsList}>
                    {laborItems.map(item => (
                      <div key={item.id} style={styles.laborItem}>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLaborItem(item.id, 'description', e.target.value)}
                          style={{ ...styles.itemInput, flex: 1 }}
                          placeholder="e.g., Demo and removal, Installation, Cleanup"
                        />
                        <div style={{ width: '120px', position: 'relative' }}>
                          <span style={styles.dollarSign}>$</span>
                          <input
                            type="number"
                            value={item.amount}
                            onChange={(e) => updateLaborItem(item.id, 'amount', parseFloat(e.target.value) || 0)}
                            style={styles.laborPriceInput}
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <button
                          style={styles.deleteButton}
                          onClick={() => deleteLaborItem(item.id)}
                          title="Delete labor item"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Labor Subtotal */}
                  <div style={styles.sectionSubtotal}>
                    <span>Labor Subtotal</span>
                    <span>${laborSubtotal.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Totals */}
            <div style={styles.totalsSection}>
              <div style={styles.totalRow}>
                <span>Subtotal (Materials + Labor)</span>
                <span style={styles.totalValue}>${subtotal.toFixed(2)}</span>
              </div>
              <div style={styles.taxRow}>
                <label style={styles.taxLabel}>
                  <input
                    type="checkbox"
                    checked={showTax}
                    onChange={(e) => setShowTax(e.target.checked)}
                    style={styles.checkbox}
                  />
                  <span>Add Tax</span>
                </label>
                {showTax && (
                  <>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      style={styles.taxInput}
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span style={{ color: '#6080a0' }}>%</span>
                    <span style={styles.taxAmount}>${taxAmount.toFixed(2)}</span>
                  </>
                )}
              </div>
              <div style={styles.grandTotalRow}>
                <span>Total</span>
                <span style={styles.grandTotalValue}>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Right Column - Client Info & Dates */}
          <div style={styles.rightColumn}>
            {/* Estimate Details */}
            <div style={styles.sectionTitle}>Estimate Details</div>

            <div style={styles.field}>
              <label style={styles.label}>Estimate Date</label>
              <input
                type="date"
                value={estimateDate}
                onChange={(e) => setEstimateDate(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Valid Until</label>
              <input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                style={styles.input}
              />
            </div>

            {/* Client Information */}
            <div style={{ ...styles.sectionTitle, marginTop: '20px' }}>Client Information</div>

            {/* Client selector dropdown */}
            {savedClients.length > 0 && (
              <div style={styles.field}>
                <label style={styles.label}>Select Client</label>
                <select
                  onChange={(e) => handleSelectClient(e.target.value)}
                  style={styles.clientSelect}
                  value=""
                >
                  <option value="">-- Select saved client --</option>
                  {savedClients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}{client.email ? ` (${client.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                style={styles.input}
                placeholder="Client name"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                style={styles.input}
                placeholder="client@email.com"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                style={styles.input}
                placeholder="(555) 123-4567"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Address (Optional)</label>
              <textarea
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                style={styles.textarea}
                placeholder="Client address"
                rows={2}
              />
            </div>

            {/* Notes Section */}
            <div style={{ ...styles.sectionTitle, marginTop: '20px' }}>Notes</div>

            <div style={styles.field}>
              <label style={styles.label}>Scope of Work (Optional)</label>
              <textarea
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                style={{ ...styles.textarea, minHeight: '80px' }}
                placeholder="Describe the work to be performed..."
                rows={3}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Terms & Conditions (Optional)</label>
              <textarea
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                style={{ ...styles.textarea, minHeight: '80px' }}
                placeholder="Payment terms, conditions, disclaimers..."
                rows={3}
              />
            </div>

            {/* Company Preview */}
            {companyProfile?.companyName && (
              <div style={styles.companyPreview}>
                <div style={styles.previewLabel}>From</div>
                <div style={styles.previewCompany}>{companyProfile.companyName}</div>
                {companyProfile.licenseNumber && (
                  <div style={styles.previewLicense}>{companyProfile.licenseNumber}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            style={{ background: '#00c8ff', color: '#000' }}
          >
            Save Estimate
          </Button>
        </div>

        {/* Save Client Prompt Modal */}
        {showSaveClientPrompt && (
          <div style={styles.promptOverlay}>
            <div style={styles.promptModal}>
              <div style={styles.promptIcon}>👤</div>
              <div style={styles.promptTitle}>Save New Client?</div>
              <div style={styles.promptText}>
                Would you like to save <strong>{clientName}</strong> to your client list for future estimates?
              </div>
              <div style={styles.promptButtons}>
                <button
                  style={styles.promptButtonSecondary}
                  onClick={() => handleSaveClientResponse(false)}
                >
                  No, just save estimate
                </button>
                <button
                  style={styles.promptButtonPrimary}
                  onClick={() => handleSaveClientResponse(true)}
                >
                  Yes, save client
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: '#1a2332',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,200,255,0.05)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerTitle: {
    color: '#00c8ff',
    fontSize: '16px',
    fontWeight: '600',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  exportButton: {
    background: 'rgba(0,200,255,0.15)',
    border: '1px solid rgba(0,200,255,0.3)',
    color: '#00c8ff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  printButton: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#c0d0e0',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  closeButton: {
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    color: '#ff6666',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '6px 10px',
    borderRadius: '4px',
  },
  content: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  leftColumn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    borderRight: '1px solid rgba(255,255,255,0.1)',
    overflow: 'hidden',
  },
  rightColumn: {
    width: '280px',
    padding: '20px',
    overflowY: 'auto',
    background: 'rgba(0,0,0,0.2)',
  },
  field: {
    marginBottom: '12px',
  },
  label: {
    display: 'block',
    fontSize: '11px',
    color: '#6080a0',
    marginBottom: '4px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'inherit',
  },
  sectionTitle: {
    color: '#00c8ff',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  lineItemsSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: '16px',
    overflow: 'hidden',
  },
  lineItemsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  addButton: {
    background: 'rgba(0,200,255,0.15)',
    border: '1px solid rgba(0,200,255,0.3)',
    color: '#00c8ff',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  columnHeaders: {
    display: 'flex',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '6px 6px 0 0',
    fontSize: '10px',
    color: '#6080a0',
    textTransform: 'uppercase',
  },
  lineItemsList: {
    flex: 1,
    overflowY: 'auto',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '0 0 6px 6px',
  },
  lineItem: {
    display: 'flex',
    gap: '8px',
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'flex-start',
  },
  itemInput: {
    width: '100%',
    padding: '6px 8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  descInput: {
    width: '100%',
    padding: '4px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6080a0',
    fontSize: '11px',
    outline: 'none',
    marginTop: '4px',
  },
  numberInput: {
    padding: '6px 8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'center',
  },
  dollarSign: {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6080a0',
    fontSize: '12px',
  },
  priceInput: {
    width: '100%',
    padding: '6px 8px 6px 20px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'right',
    boxSizing: 'border-box',
  },
  lineTotal: {
    width: '90px',
    padding: '6px 0',
    color: '#00c8ff',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'right',
  },
  deleteButton: {
    width: '32px',
    height: '32px',
    background: 'rgba(255,100,100,0.1)',
    border: 'none',
    borderRadius: '4px',
    color: '#ff6464',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#6080a0',
    fontSize: '13px',
  },
  sectionSubtotal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: 'rgba(0,200,255,0.1)',
    borderRadius: '6px',
    color: '#00c8ff',
    fontSize: '13px',
    fontWeight: '500',
    marginTop: '8px',
  },
  laborSection: {
    marginTop: '20px',
  },
  laborColumnHeaders: {
    display: 'flex',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '6px 6px 0 0',
    fontSize: '10px',
    color: '#6080a0',
    textTransform: 'uppercase',
  },
  laborItemsList: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '0 0 6px 6px',
  },
  laborItem: {
    display: 'flex',
    gap: '8px',
    padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    alignItems: 'center',
  },
  laborPriceInput: {
    width: '100%',
    padding: '6px 8px 6px 20px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'right',
    boxSizing: 'border-box',
  },
  totalsSection: {
    marginTop: '16px',
    padding: '16px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '8px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    color: '#c0d0e0',
    fontSize: '14px',
  },
  totalValue: {
    fontWeight: '500',
  },
  taxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 0',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  taxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#c0d0e0',
    fontSize: '14px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
  },
  taxInput: {
    width: '60px',
    padding: '4px 8px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '13px',
    outline: 'none',
    textAlign: 'right',
  },
  taxAmount: {
    marginLeft: 'auto',
    color: '#c0d0e0',
  },
  grandTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0 0 0',
    marginTop: '8px',
    borderTop: '2px solid rgba(0,200,255,0.3)',
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
  },
  grandTotalValue: {
    color: '#00c8ff',
    fontSize: '20px',
  },
  companyPreview: {
    marginTop: '20px',
    padding: '12px',
    background: 'rgba(0,200,255,0.05)',
    border: '1px solid rgba(0,200,255,0.2)',
    borderRadius: '8px',
  },
  previewLabel: {
    fontSize: '10px',
    color: '#6080a0',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  previewCompany: {
    color: '#00c8ff',
    fontSize: '14px',
    fontWeight: '600',
  },
  previewLicense: {
    color: '#6080a0',
    fontSize: '11px',
    marginTop: '4px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  // Client selector styles
  clientSelect: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'rgba(0,200,255,0.1)',
    border: '1px solid rgba(0,200,255,0.3)',
    borderRadius: '6px',
    color: '#00c8ff',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  // Save client prompt styles
  promptOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    borderRadius: '12px',
  },
  promptModal: {
    backgroundColor: '#1a2332',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    maxWidth: '360px',
    border: '1px solid rgba(0,200,255,0.3)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  promptIcon: {
    fontSize: '40px',
    marginBottom: '16px',
  },
  promptTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  promptText: {
    color: '#6080a0',
    fontSize: '14px',
    lineHeight: '1.5',
    marginBottom: '20px',
  },
  promptButtons: {
    display: 'flex',
    gap: '12px',
  },
  promptButtonSecondary: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: '#8899aa',
    fontSize: '13px',
    cursor: 'pointer',
  },
  promptButtonPrimary: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: '#00c8ff',
    border: 'none',
    borderRadius: '6px',
    color: '#000',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

// Mobile-specific styles
const mobileStyles = {
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#0c1218',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: 'rgba(0,200,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#ff6666',
    fontSize: '24px',
    padding: '4px 8px',
    cursor: 'pointer',
  },
  headerTitle: {
    color: '#00c8ff',
    fontSize: '16px',
    fontWeight: '600',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  actionBtn: {
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  tabBar: {
    display: 'flex',
    background: 'rgba(0,0,0,0.3)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  tab: {
    flex: 1,
    padding: '14px 16px',
    background: 'none',
    border: 'none',
    color: '#6080a0',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
  },
  tabActive: {
    color: '#00c8ff',
    borderBottomColor: '#00c8ff',
    background: 'rgba(0,200,255,0.05)',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
  },
  scrollContainer: {
    height: '100%',
    overflowY: 'auto',
    padding: '16px',
    paddingBottom: '100px', // Extra space for footer
  },
  section: {
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  sectionTitle: {
    color: '#00c8ff',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  addBtn: {
    background: 'rgba(0,200,255,0.2)',
    border: '1px solid rgba(0,200,255,0.4)',
    color: '#00c8ff',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '12px',
    color: '#6080a0',
    marginBottom: '6px',
    textTransform: 'uppercase',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'none',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'rgba(0,200,255,0.1)',
    border: '1px solid rgba(0,200,255,0.3)',
    borderRadius: '10px',
    color: '#00c8ff',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  emptyState: {
    padding: '32px 20px',
    textAlign: 'center',
    color: '#6080a0',
    fontSize: '14px',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '10px',
  },
  lineItem: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '10px',
    padding: '14px',
    marginBottom: '10px',
    border: '1px solid rgba(255,255,255,0.05)',
  },
  lineItemHeader: {
    display: 'flex',
    gap: '10px',
    marginBottom: '8px',
  },
  itemNameInput: {
    flex: 1,
    padding: '12px 14px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '15px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  descInput: {
    width: '100%',
    padding: '8px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#6080a0',
    fontSize: '13px',
    outline: 'none',
    marginBottom: '10px',
    boxSizing: 'border-box',
  },
  deleteBtn: {
    width: '44px',
    height: '44px',
    background: 'rgba(255,100,100,0.15)',
    border: 'none',
    borderRadius: '8px',
    color: '#ff6464',
    fontSize: '22px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  lineItemRow: {
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-end',
  },
  fieldGroup: {
    flex: 1,
  },
  fieldLabel: {
    display: 'block',
    fontSize: '10px',
    color: '#6080a0',
    marginBottom: '4px',
    textTransform: 'uppercase',
  },
  smallInput: {
    width: '100%',
    padding: '10px 8px',
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '6px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  lineTotal: {
    minWidth: '80px',
    padding: '10px 8px',
    color: '#00c8ff',
    fontSize: '15px',
    fontWeight: '600',
    textAlign: 'right',
    background: 'rgba(0,200,255,0.1)',
    borderRadius: '6px',
  },
  laborItem: {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '10px',
    padding: '14px',
    marginBottom: '10px',
    border: '1px solid rgba(255,255,255,0.05)',
  },
  laborItemHeader: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  laborAmountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subtotalBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    background: 'rgba(0,200,255,0.1)',
    borderRadius: '10px',
    color: '#00c8ff',
    fontSize: '15px',
    fontWeight: '500',
    marginTop: '12px',
  },
  totalsSection: {
    background: 'rgba(0,0,0,0.3)',
    borderRadius: '12px',
    padding: '16px',
    marginTop: '8px',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    color: '#c0d0e0',
    fontSize: '15px',
  },
  taxRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  taxLabel: {
    display: 'flex',
    alignItems: 'center',
    color: '#c0d0e0',
    fontSize: '15px',
    cursor: 'pointer',
  },
  grandTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0 4px 0',
    marginTop: '8px',
    borderTop: '2px solid rgba(0,200,255,0.4)',
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
  },
  grandTotalValue: {
    color: '#00c8ff',
    fontSize: '22px',
    fontWeight: '700',
  },
  companyPreview: {
    marginTop: '20px',
    padding: '16px',
    background: 'rgba(0,200,255,0.05)',
    border: '1px solid rgba(0,200,255,0.2)',
    borderRadius: '12px',
  },
  previewLabel: {
    fontSize: '11px',
    color: '#6080a0',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  previewCompany: {
    color: '#00c8ff',
    fontSize: '16px',
    fontWeight: '600',
  },
  previewLicense: {
    color: '#6080a0',
    fontSize: '13px',
    marginTop: '4px',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.3)',
  },
  cancelBtn: {
    flex: 1,
    padding: '16px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '10px',
    color: '#8899aa',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  saveBtn: {
    flex: 2,
    padding: '16px',
    background: '#00c8ff',
    border: 'none',
    borderRadius: '10px',
    color: '#000',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default EstimateEditor;
